import "./i18n/config";

import { ApolloProvider } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import i18next from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { createBrowserRouter, RouterProvider, useRouteError } from "react-router";

import { AppRoutes } from "./AppRoutes";
import { Loading } from "./common/components";
import { LocalStorageProvider, UserPreferencesProvider } from "./common/contexts";
import { completeConnectCallback, isConnectCallback } from "./features/authn/connectAuth";
import { AuthContext, AuthProvider } from "./features/authn/context";
import { TenantProvider } from "./features/tenant/context";
import { getClientWithAuthenticatedFetch } from "./lib/apolloClient";
import { initFaro } from "./lib/faro";
import { ErrorFallback, initSentry, setSentryScope } from "./lib/sentry";

const connectCallback = isConnectCallback();
const connectInitialization = connectCallback ? completeConnectCallback() : undefined;
// Connectのcodeを従来のSDKやtelemetryへ渡さない。
if (!connectCallback) {
  initSentry();
  initFaro();
}

const router = createBrowserRouter([{ path: "*", element: <AppRoutes />, errorElement: <RouterErrorFallback /> }]);

function RouterErrorFallback() {
  const error = useRouteError();
  return <ErrorFallback error={error} componentStack="" eventId="" resetError={() => window.location.reload()} />;
}

const handleRouterError: React.ComponentProps<typeof RouterProvider>["onError"] = (error, { errorInfo }) => {
  Sentry.withScope((scope) => {
    setSentryScope(scope, error);
    if (errorInfo?.componentStack) scope.setExtra("componentStack", errorInfo.componentStack);
    Sentry.captureException(error);
  });
};

export const App = () => {
  const [callbackReady, setCallbackReady] = React.useState(!connectCallback);
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;

  React.useEffect(() => {
    document.documentElement.lang = language;
    const onChange = (lng: string) => {
      document.documentElement.lang = lng;
    };
    i18next.on("languageChanged", onChange);
    return () => {
      i18next.off("languageChanged", onChange);
    };
  }, [language]);

  React.useEffect(() => {
    if (connectInitialization) void connectInitialization.catch(() => undefined).finally(() => setCallbackReady(true));
  }, []);

  if (!callbackReady) return <Loading />;

  return (
    <React.StrictMode>
      <Sentry.ErrorBoundary
        fallback={({ error, componentStack, resetError, eventId }) => (
          <ErrorFallback eventId={eventId} error={error} componentStack={componentStack} resetError={resetError} />
        )}
        beforeCapture={(scope, error) => {
          setSentryScope(scope, error);
        }}
      >
        <LocalStorageProvider>
          <AuthProvider loadingComponent={<Loading />}>
            <TenantProvider>
              <AuthContext.Consumer>
                {({ authenticatedFetch }) => (
                  <ApolloProvider client={getClientWithAuthenticatedFetch(authenticatedFetch)}>
                    <UserPreferencesProvider>
                      <RouterProvider router={router} onError={handleRouterError} />
                    </UserPreferencesProvider>
                  </ApolloProvider>
                )}
              </AuthContext.Consumer>
            </TenantProvider>
          </AuthProvider>
        </LocalStorageProvider>
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
};
